/**
 * Internal traffic filter — keeps Memory Lane's own team out of the
 * analytics signal (Microsoft Clarity replays + heatmaps, Google
 * Analytics 4 events, Live Users).
 *
 * The filter answers ONE question: "is this session internal?" — and
 * everywhere we boot a tracker (Clarity) or fire an event (GA4) we ask
 * first. Internal sessions are silently dropped client-side; no network
 * traffic ever reaches Clarity or GA4, so the founder's own QA work
 * can't pollute heatmaps or inflate Active Users.
 *
 * ──────────────────────────────────────────────────────────────────────
 * HOW TO ADD A NEW INTERNAL USER WITHOUT CHANGING CODE
 * ──────────────────────────────────────────────────────────────────────
 *
 *   Option A — set environment variables in /app/frontend/.env (build-time):
 *
 *     REACT_APP_INTERNAL_EMAILS=ashlee@example.com,reviewer@example.com
 *     REACT_APP_INTERNAL_DOMAINS=@yourdomain.com,@trusted-tester.com
 *     REACT_APP_INTERNAL_ROLES=admin,tester
 *
 *   Option B — set localStorage at runtime (no rebuild, per browser):
 *
 *     localStorage.setItem('ml_internal_emails',
 *       JSON.stringify(['extra@example.com']));
 *     localStorage.setItem('ml_internal_optout', '1');  // force exclude
 *
 *   Option C — extend the hardcoded fallbacks at the bottom of this
 *   file. (Use Option A whenever possible so the filter is consistent
 *   across browsers / devices for the same teammate.)
 *
 * ──────────────────────────────────────────────────────────────────────
 *  WHAT COUNTS AS "INTERNAL"
 * ──────────────────────────────────────────────────────────────────────
 *   1. Email matches an entry in the internal-email list.
 *   2. Email's domain matches an entry in the internal-domain list
 *      (e.g. anyone @emergent.sh / @emergentagent.com).
 *   3. The cached user_context.user_type === "admin"  OR the user.role
 *      is in the internal-roles list.
 *   4. The browser is on a non-production host (localhost, preview
 *      domains, *.emergentagent.com, IP literals).
 *   5. The teammate has opted themselves out via the localStorage flag
 *      `ml_internal_optout=1`. Useful for personal devices.
 *
 *  Anyone matching ANY of these rules is filtered. The check is
 *  intentionally cheap (string compares only) so calling it on every
 *  event isn't a perf concern.
 */

// ---- Hardcoded fallbacks (safe defaults; override via env or localStorage) --
// Keep these short — the runtime config is what scales.
const DEFAULT_INTERNAL_EMAILS = [
    "admin@memorylane.app",
    "yc@memorylane.app",
];

const DEFAULT_INTERNAL_DOMAINS = [
    // Memory Lane builder / hosting platform — anyone signed in with
    // an @emergent.sh or @emergentagent.com address is not a real user.
    "@emergent.sh",
    "@emergentagent.com",
    "@emergent.com",
];

const DEFAULT_INTERNAL_ROLES = [
    "admin",
];

// Hosts that should NEVER count as production traffic. Anything matched
// here is filtered regardless of who's signed in.
const NON_PRODUCTION_HOST_PATTERNS = [
    /^localhost$/i,
    /^127\.0\.0\.1$/,
    /^0\.0\.0\.0$/,
    /^192\.168\./,
    /^10\./,
    /\.local$/i,
    /\.preview\.emergentagent\.com$/i,
    /\.emergentagent\.com$/i,
    /^staging\./i,
    /^dev\./i,
];

// ---- Env / localStorage loaders -------------------------------------------

function parseCsv(value) {
    if (!value || typeof value !== "string") return [];
    return value
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
}

function readLocalJsonArray(key) {
    if (typeof window === "undefined") return [];
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((s) => String(s).trim().toLowerCase()).filter(Boolean);
    } catch {
        return [];
    }
}

function getInternalEmails() {
    const envList = parseCsv(process.env.REACT_APP_INTERNAL_EMAILS);
    const localList = readLocalJsonArray("ml_internal_emails");
    return Array.from(new Set([
        ...DEFAULT_INTERNAL_EMAILS.map((e) => e.toLowerCase()),
        ...envList,
        ...localList,
    ]));
}

function getInternalDomains() {
    const envList = parseCsv(process.env.REACT_APP_INTERNAL_DOMAINS).map((d) =>
        d.startsWith("@") ? d : `@${d}`
    );
    const localList = readLocalJsonArray("ml_internal_domains").map((d) =>
        d.startsWith("@") ? d : `@${d}`
    );
    return Array.from(new Set([
        ...DEFAULT_INTERNAL_DOMAINS.map((d) => d.toLowerCase()),
        ...envList,
        ...localList,
    ]));
}

function getInternalRoles() {
    const envList = parseCsv(process.env.REACT_APP_INTERNAL_ROLES);
    const localList = readLocalJsonArray("ml_internal_roles");
    return Array.from(new Set([
        ...DEFAULT_INTERNAL_ROLES.map((r) => r.toLowerCase()),
        ...envList,
        ...localList,
    ]));
}

// ---- Detection primitives -------------------------------------------------

export function isInternalEmail(email) {
    if (!email || typeof email !== "string") return false;
    const lower = email.trim().toLowerCase();
    if (!lower) return false;
    const emails = getInternalEmails();
    if (emails.includes(lower)) return true;
    const domains = getInternalDomains();
    return domains.some((d) => lower.endsWith(d));
}

export function isInternalRole(role) {
    if (!role || typeof role !== "string") return false;
    return getInternalRoles().includes(role.trim().toLowerCase());
}

export function isInternalHost() {
    if (typeof window === "undefined") return false;
    const host = (window.location.hostname || "").toLowerCase();
    if (!host) return false;
    return NON_PRODUCTION_HOST_PATTERNS.some((rx) => rx.test(host));
}

/**
 * Cached snapshot read from /lib/analytics.js — see syncUserContext in
 * /lib/auth.jsx. Reading this means we honour the "admin opens any
 * page" case even when the URL doesn't say /admin.
 */
function readCachedUserContext() {
    if (typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem("mlcn_ga_user_context");
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

/**
 * Manual opt-out flag (set by the teammate on their own browser).
 *   localStorage.setItem('ml_internal_optout', '1')
 */
function hasManualOptOut() {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem("ml_internal_optout") === "1";
    } catch {
        return false;
    }
}

// ---- Public API -----------------------------------------------------------

/**
 * `user` argument is optional. When omitted we still detect via:
 *   - cached user_context (set by auth.jsx after login)
 *   - the hostname (non-production environments)
 *   - manual opt-out flag in localStorage
 *
 * Pass a user object (`{ email, role, ... }`) when you have one handy
 * for the most accurate decision (e.g. inside the auth provider).
 */
export function isInternalUser(user) {
    // DebugView override — lets us verify GA4 events from preview/dev:
    //   localStorage.setItem('ml_analytics_debug', '1')
    // Events then fire with debug_mode:true (see analytics.js) so they
    // appear in GA4 DebugView, tagged traffic_type=internal_debug.
    // NOTE: this bypass applies to GA4 events ONLY — Clarity session
    // recording uses isInternalUserStrict() below so preview/QA
    // sessions never pollute production Clarity recordings.
    try {
        if (typeof window !== "undefined" &&
            window.localStorage.getItem("ml_analytics_debug") === "1") return false;
    } catch { /* storage disabled */ }
    return isInternalUserStrict(user);
}

/**
 * Same as isInternalUser but WITHOUT the ml_analytics_debug bypass.
 * Used to gate Microsoft Clarity session recording — the debug flag is
 * a GA4 DebugView tool and must never cause preview/localhost/admin
 * sessions to be recorded into the production Clarity project.
 */
export function isInternalUserStrict(user) {
    if (hasManualOptOut()) return true;
    if (isInternalHost()) return true;

    if (user && typeof user === "object") {
        if (isInternalEmail(user.email)) return true;
        if (isInternalRole(user.role)) return true;
    }

    // Fall back to whatever the auth provider last cached.
    const ctx = readCachedUserContext();
    if (ctx?.user_type === "admin") return true;
    if (isInternalEmail(ctx?.email)) return true;
    if (isInternalRole(ctx?.role)) return true;

    return false;
}

/**
 * One-line `if (shouldDropEvent()) return;` guard for analytics helpers.
 * Identical to isInternalUser() with no args — kept as a named export so
 * callsites read as intent ("drop internal events") rather than
 * implementation ("check if internal user").
 */
export function shouldDropEvent() {
    return isInternalUser();
}

// ---- Debug helpers (handy in the console) ---------------------------------
if (typeof window !== "undefined") {
    window.__mlInternalDebug = () => ({
        host: window.location.hostname,
        isInternalHost: isInternalHost(),
        manualOptOut: hasManualOptOut(),
        cachedUserContext: readCachedUserContext(),
        internalEmails: getInternalEmails(),
        internalDomains: getInternalDomains(),
        internalRoles: getInternalRoles(),
        decision_isInternalUser: isInternalUser(),
    });
}
